import { useState, useRef, useCallback, useEffect } from 'react'
import { firebase_auth } from '../common/firebaseConfig'
import {
  MessageSegment,
  appendTextToSegments,
  startToolSegment,
  updateToolSegment,
} from './chatSegments'
import { captureEvent } from '../posthog'

const chatSurface = (segmented: boolean) => (segmented ? 'builder_copilot' : 'backtest_chat')

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  toolCalls?: Array<{ name: string; input: any; result: any }>
  /** Present only in segmented mode: ordered text + inline tool segments. */
  segments?: MessageSegment[]
}

/** The frozen tool_result SSE event's payload as read client-side. `data` is
 * optional and only populated by draft-aware backend tools. */
export interface ToolResultEventPayload {
  toolName: string
  toolCallId: string
  success: boolean
  summary: string
  data?: unknown
}

export interface ChatAssistantOptions {
  /** Keep narration across tool calls and expose ordered message segments.
   * Default false preserves the legacy ChatAssistant behavior exactly. */
  segmented?: boolean
  /** Receives the FULL tool_result payload (the default path only keeps summary). */
  onToolResult?: (payload: ToolResultEventPayload) => void
}

export interface ActiveToolCall {
  toolName: string
  toolCallId: string
  status: 'started' | 'executing' | 'done'
}

export interface CompletedToolCall {
  toolName: string
  toolCallId: string
  summary: string
}

interface SSEEvent {
  event: string
  data: any
}

const FUNCTION_BASE_URL =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? 'https://us-central1-greenchart-93a2a.cloudfunctions.net'
    : 'https://us-central1-greenchart-dev.cloudfunctions.net'

export function parseSSEChunk(buffer: string): { events: SSEEvent[]; remainder: string } {
  const events: SSEEvent[] = []
  const lines = buffer.split('\n')
  let currentEvent = ''
  let currentData = ''
  let remainder = ''

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    if (line.startsWith('event: ')) {
      currentEvent = line.slice(7)
    } else if (line.startsWith('data: ')) {
      currentData = line.slice(6)
    } else if (line === '' && currentEvent && currentData) {
      try {
        events.push({ event: currentEvent, data: JSON.parse(currentData) })
      } catch {
        events.push({ event: currentEvent, data: currentData })
      }
      currentEvent = ''
      currentData = ''
    } else if (line === '' && !currentEvent && !currentData) {
      // Empty line separator, ignore
    } else {
      // Incomplete data at end of buffer
      const remaining = lines.slice(i).join('\n')
      if (remaining.trim()) {
        remainder = remaining
      }
      break
    }
  }

  // If we have partial event data at the end, keep it as remainder
  if (currentEvent || currentData) {
    const partial = []
    if (currentEvent) partial.push(`event: ${currentEvent}`)
    if (currentData) partial.push(`data: ${currentData}`)
    remainder = partial.join('\n') + (remainder ? '\n' + remainder : '')
  }

  return { events, remainder }
}

export interface ChatContext {
  backtestId?: string
  alertId?: string
  patternId?: string
  patternVersion?: number
}

export default function useChatAssistant(
  tradingHouse: 'gtf' | 'greenchart',
  context?: ChatContext,
  options?: ChatAssistantOptions
) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  // Ref so callback identity churn in options never re-creates sendMessage
  const optionsRef = useRef<ChatAssistantOptions | undefined>(options)
  optionsRef.current = options
  const [isStreaming, setIsStreaming] = useState(false)
  const [activeToolCalls, setActiveToolCalls] = useState<ActiveToolCall[]>([])
  const [completedToolCalls, setCompletedToolCalls] = useState<CompletedToolCall[]>([])
  const [conversationId, setConversationId] = useState<string | null>(null)
  const turnMetricsRef = useRef({ startedAt: 0, toolCalls: 0 })
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const streamingTextRef = useRef('')

  // Abort any in-flight stream on unmount — otherwise the fetch keeps reading
  // (and billing) against a dead hook
  useEffect(() => () => abortControllerRef.current?.abort(), [])

  // Segmented mode: a stopped/errored turn must not leave tool chips spinning
  const finalizeInFlightToolSegments = useCallback(() => {
    if (!optionsRef.current?.segmented) return
    setMessages((prev) => {
      const lastIdx = prev.length - 1
      if (lastIdx < 0 || prev[lastIdx].role !== 'assistant' || !prev[lastIdx].segments) return prev
      const updated = [...prev]
      updated[lastIdx] = {
        ...updated[lastIdx],
        segments: (updated[lastIdx].segments ?? []).map((segment) =>
          segment.type === 'tool' && segment.status !== 'done'
            ? { ...segment, status: 'done' as const, success: false, summary: 'Stopped' }
            : segment
        ),
      }
      return updated
    })
  }, [])

  const loadConversation = useCallback(
    (loadedMessages: ChatMessage[], loadedConversationId: string) => {
      setMessages(loadedMessages)
      setConversationId(loadedConversationId)
    },
    []
  )

  const sendMessage = useCallback(
    async (message: string) => {
      if (isStreaming) return

      setError(null)
      setIsStreaming(true)
      setCompletedToolCalls([])
      streamingTextRef.current = ''

      // Add optimistic user message
      const userMessage: ChatMessage = { role: 'user', content: message }
      setMessages((prev) => [...prev, userMessage])

      // Add empty assistant message for streaming
      const segmented = !!optionsRef.current?.segmented
      setMessages((prev) => [
        ...prev,
        segmented
          ? { role: 'assistant', content: '', segments: [] }
          : { role: 'assistant', content: '' },
      ])

      try {
        const idToken = await firebase_auth.currentUser?.getIdToken()
        if (!idToken) {
          throw new Error('Not authenticated')
        }

        const controller = new AbortController()
        abortControllerRef.current = controller

        const response = await fetch(`${FUNCTION_BASE_URL}/chatAssistant`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            conversationId,
            message,
            tradingHouse,
            context,
          }),
          signal: controller.signal,
        })

        if (!response.ok) {
          const errorBody = await response.text()
          throw new Error(errorBody || `HTTP ${response.status}`)
        }

        turnMetricsRef.current = { startedAt: Date.now(), toolCalls: 0 }
        captureEvent('copilot_message_sent', {
          surface: chatSurface(segmented),
          is_new_conversation: !conversationId,
          has_backtest_context: Boolean(context?.backtestId),
          has_pattern_context: Boolean(context?.patternId),
          has_alert_context: Boolean(context?.alertId),
        })

        const reader = response.body?.getReader()
        if (!reader) throw new Error('No response body')

        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const { events, remainder } = parseSSEChunk(buffer)
          buffer = remainder

          for (const evt of events) {
            switch (evt.event) {
              case 'connected':
                setConversationId(evt.data.conversationId)
                break

              case 'text_delta':
                streamingTextRef.current += evt.data.text
                setMessages((prev) => {
                  const updated = [...prev]
                  const lastIdx = updated.length - 1
                  if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                    updated[lastIdx] = {
                      ...updated[lastIdx],
                      content: streamingTextRef.current,
                      ...(segmented
                        ? {
                            segments: appendTextToSegments(
                              updated[lastIdx].segments ?? [],
                              evt.data.text
                            ),
                          }
                        : {}),
                    }
                  }
                  return updated
                })
                break

              case 'tool_start':
                // Legacy mode: text accumulated before a tool call is just Claude
                // narrating ("Let me look that up...") — clear it. Segmented mode
                // PRESERVES narration and records the tool inline instead.
                if (!segmented && streamingTextRef.current.trim()) {
                  streamingTextRef.current = ''
                  setMessages((prev) => {
                    const updated = [...prev]
                    const lastIdx = updated.length - 1
                    if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                      updated[lastIdx] = { ...updated[lastIdx], content: '' }
                    }
                    return updated
                  })
                }
                if (segmented) {
                  setMessages((prev) => {
                    const updated = [...prev]
                    const lastIdx = updated.length - 1
                    if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                      updated[lastIdx] = {
                        ...updated[lastIdx],
                        segments: startToolSegment(
                          updated[lastIdx].segments ?? [],
                          evt.data.toolName,
                          evt.data.toolCallId
                        ),
                      }
                    }
                    return updated
                  })
                }
                setActiveToolCalls((prev) => [
                  ...prev,
                  {
                    toolName: evt.data.toolName,
                    toolCallId: evt.data.toolCallId,
                    status: 'started',
                  },
                ])
                break

              case 'tool_executing':
                if (segmented) {
                  setMessages((prev) => {
                    const updated = [...prev]
                    const lastIdx = updated.length - 1
                    if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                      updated[lastIdx] = {
                        ...updated[lastIdx],
                        segments: updateToolSegment(
                          updated[lastIdx].segments ?? [],
                          evt.data.toolCallId,
                          { status: 'executing' }
                        ),
                      }
                    }
                    return updated
                  })
                }
                setActiveToolCalls((prev) =>
                  prev.map((tc) =>
                    tc.toolCallId === evt.data.toolCallId ? { ...tc, status: 'executing' } : tc
                  )
                )
                break

              case 'tool_result':
                if (segmented) {
                  setMessages((prev) => {
                    const updated = [...prev]
                    const lastIdx = updated.length - 1
                    if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                      updated[lastIdx] = {
                        ...updated[lastIdx],
                        segments: updateToolSegment(
                          updated[lastIdx].segments ?? [],
                          evt.data.toolCallId,
                          {
                            status: 'done',
                            success: evt.data.success !== false,
                            summary: evt.data.summary || '',
                          }
                        ),
                      }
                    }
                    return updated
                  })
                }
                setActiveToolCalls((prev) =>
                  prev.filter((tc) => tc.toolCallId !== evt.data.toolCallId)
                )
                setCompletedToolCalls((prev) => [
                  ...prev,
                  {
                    toolName: evt.data.toolName,
                    toolCallId: evt.data.toolCallId,
                    summary: evt.data.summary || '',
                  },
                ])
                turnMetricsRef.current.toolCalls += 1
                optionsRef.current?.onToolResult?.(evt.data as ToolResultEventPayload)
                break

              case 'iteration':
                // New iteration — Claude will respond again after tool results.
                // Do NOT reset streamingTextRef here; text accumulates across iterations
                // so the user sees a continuous response.
                break

              case 'done':
                captureEvent('copilot_response_completed', {
                  surface: chatSurface(segmented),
                  tool_call_count: turnMetricsRef.current.toolCalls,
                  duration_ms: Date.now() - turnMetricsRef.current.startedAt,
                })
                setIsStreaming(false)
                setActiveToolCalls([])
                break

              case 'error':
                captureEvent('copilot_response_failed', {
                  surface: chatSurface(segmented),
                  stage: 'stream',
                })
                setError(evt.data.message)
                setIsStreaming(false)
                setActiveToolCalls([])
                finalizeInFlightToolSegments()
                break
            }
          }
        }
      } catch (err: any) {
        if (err.name === 'AbortError') {
          // User cancelled
        } else {
          console.error('Chat assistant error:', err)
          captureEvent('copilot_response_failed', {
            surface: chatSurface(segmented),
            stage: 'request',
          })
          setError(err.message || 'An unexpected error occurred')
          // Update the last assistant message with error
          setMessages((prev) => {
            const updated = [...prev]
            const lastIdx = updated.length - 1
            if (
              lastIdx >= 0 &&
              updated[lastIdx].role === 'assistant' &&
              !updated[lastIdx].content
            ) {
              updated[lastIdx] = {
                ...updated[lastIdx],
                content: 'Sorry, I encountered an error. Please try again.',
              }
            }
            return updated
          })
        }
        setIsStreaming(false)
        setActiveToolCalls([])
        finalizeInFlightToolSegments()
      }
    },
    [isStreaming, conversationId, tradingHouse, context, finalizeInFlightToolSegments]
  )

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort()
    setIsStreaming(false)
    setActiveToolCalls([])
    finalizeInFlightToolSegments()
  }, [finalizeInFlightToolSegments])

  const resetConversation = useCallback(() => {
    setMessages([])
    setConversationId(null)
    setError(null)
    setActiveToolCalls([])
    setCompletedToolCalls([])
    streamingTextRef.current = ''
  }, [])

  return {
    messages,
    sendMessage,
    cancel,
    resetConversation,
    loadConversation,
    isStreaming,
    activeToolCalls,
    completedToolCalls,
    conversationId,
    error,
  }
}
